<script setup lang="ts">
import VuePdfEmbed from 'vue-pdf-embed'
import SignArtworkReviewDialog from '@/components/projects/signs/sign-modal/sign-artwork-review/SignArtworkReviewDialog.vue'
import { useSnackbarStore } from '@/store/snackbar'
import { downloadArtworkOrProof } from '@/helpers/utils/downloadAllFilesUsingLinks'
import { getPreferredPdfAiLink } from '@/helpers/utils/dropboxLinks'
import { getDownloadLinkMetadata } from '@/helpers/utils/downloadLinkMetadata'

interface IProps {
  sign?: any,
  signSides?: number,
  sides?: string[],
  showSignArtworkReviewDialog?: boolean,
  fileType?: 'artwork' | 'prepress_proof',
}

const props = withDefaults(defineProps<IProps>(), {
  sign: null,
  signSides: 1,
  sides: () => (['A', 'B', 'C', 'D']),
  showSignArtworkReviewDialog: false,
  fileType: 'artwork',
})

interface CustomEvents {
  (e: 'update:modelValue', value: boolean): void
  (e: 'update:showSignArtworkReviewDialog', value: boolean): void
  (e: 'onClose'): void
  (e: 'onArtworkPdfRendered'): void
}

const emit = defineEmits<CustomEvents>()

const snackbarStore = useSnackbarStore()

const page = ref<number>(1)
const totalPages = ref<number>(1)
const artworkPdf = ref<any>()
const artworkLoading = ref<boolean>(true)

const isHoveredArtworkBlock = ref<boolean>(false)

const currentFileLinks = computed(() => {
  if (props.fileType === 'prepress_proof') {
    return Array.isArray(props.sign?.prepress_proof_file_links) ? props.sign.prepress_proof_file_links : []
  }
  return Array.isArray(props.sign?.artwork_file_links) ? props.sign.artwork_file_links : []
})

// True when a download source is available (S3 files)
const hasDownloadSource = computed(() => {
  return currentFileLinks.value.length > 0
})

const downloadTypeLabel = computed(() => (
  props.fileType === 'prepress_proof' ? 'Prepress Proof' : 'Artwork'
))

const downloadInfo = computed(() => getDownloadLinkMetadata(
  currentFileLinks.value,
  downloadTypeLabel.value,
))

const mainFilePdfLink = computed(() => {
  const links = currentFileLinks.value
    .map((item: any) => item?.url)
    .filter((url: any): url is string => typeof url === 'string' && !!url)

  if (!links.length) {
    return null
  }

  return getPreferredPdfAiLink(links)
})

watch(mainFilePdfLink, link => {
  artworkLoading.value = !!link
}, { immediate: true })

const handleArtworkRendered = () => {
  artworkLoading.value = false
  totalPages.value = artworkPdf.value.pageCount
  emit('onArtworkPdfRendered')
}

const handleArtworkClick = () => {
  emit('update:showSignArtworkReviewDialog', true)
}

onBeforeUnmount(() => {
  // no-op
})

const downloadSignFiles = async (sign: any) => {
  const isProof = props.fileType === 'prepress_proof'
  const links = isProof
    ? (Array.isArray(sign?.prepress_proof_file_links) ? sign.prepress_proof_file_links : [])
    : (Array.isArray(sign?.artwork_file_links) ? sign.artwork_file_links : [])
  const downloadLinks = links
    .map((item: any) => item?.url)
    .filter((url: any): url is string => typeof url === 'string' && !!url)

  if (!downloadLinks.length) {
    snackbarStore.showMessage({
      color: 'warning',
      icon: 'mdi-alert-circle',
      title: 'Warning',
      text: 'No valid links found'
    })
    return
  }
  try {
    const result = await downloadArtworkOrProof(
      sign?.campaign,
      sign?.main_full_code || sign?.short_name,
      isProof ? 'prepress_proof' : 'artwork',
    )
    if (result.status === 'complete') {
      snackbarStore.showMessage({
        color: 'success',
        icon: 'mdi-checkbox-marked-circle',
        title: 'Download Complete',
        text: 'File downloaded successfully!'
      })
    } else {
      snackbarStore.showMessage({
        color: 'error',
        icon: 'mdi-alert-circle',
        title: 'Download Failed',
        text: 'Failed to process download request'
      })
    }
  } catch (e: any) {
    console.log('Download error:', e)
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Download Failed',
      text: 'Failed to process download requests'
    })
  }
}
</script>

<template>
  <div>
    <SignArtworkReviewDialog
      :model-value="showSignArtworkReviewDialog"
      :sign="sign"
      @update:modelValue="newValue => emit('update:showSignArtworkReviewDialog', newValue)"
    />
    <div v-if="!hasDownloadSource">
      <div class="pa-2 text-center">
        {{ props.fileType === 'prepress_proof' ? 'No Prepress Proof' : 'No Artwork' }}
      </div>
    </div>
    <div
      v-else
      :class="{'min-height-200': artworkLoading}"
    >
      <VOverlay
        :model-value="artworkLoading"
        class="align-center justify-center"
        contained
        persistent
      >
        <VProgressCircular
          color="primary"
          indeterminate
        />
      </VOverlay>
      <div v-if="!artworkLoading" class="d-flex justify-space-between align-center">
        <div class="d-flex align-center justify-start">
          <a
            target="_blank"
            href="#"
            @click.prevent="downloadSignFiles(sign)"
          >
            {{ downloadInfo.label }}
            <VMenu
              v-if="downloadInfo.hasFiles"
              activator="parent"
              location="top center"
              open-on-hover
              open-on-focus
              open-delay="100"
              close-delay="0"
              offset="5"
              class="default-tooltip"
            >
              <span class="px-2">
                {{ downloadInfo.tooltipLine }}
              </span>
            </VMenu>
          </a>
        </div>
        <div
          v-if="sign?.total_annotations_count && !['prepress_proof'].includes(props.fileType)"
          class="d-flex align-center justify-end"
        >
          Annotations:
          {{ sign.total_annotations_count }}
          <div class="position-relative">
            <VIcon
              size="18"
              icon="tabler-info-circle"
              class="ml-1"
            />
            <VMenu
              activator="parent"
              location="top center"
              open-on-hover
              open-delay="100"
              close-delay="0"
              offset="5"
              class="default-tooltip"
            >
              <span class="px-2">
                Total: {{ sign.total_annotations_count }}<br>
                Unresolved: {{ sign.unresolved_annotations_count }}
              </span>
            </VMenu>
          </div>
        </div>
      </div>
      <div
        v-if="mainFilePdfLink"
        class="artwork-block"
        @mouseover="isHoveredArtworkBlock = true"
        @mouseleave="isHoveredArtworkBlock = false"
        @click="handleArtworkClick"
      >
        <VuePdfEmbed
          ref="artworkPdf"
          :page="page"
          :source="mainFilePdfLink"
          @rendered="handleArtworkRendered"
        />
        <transition name="fade">
          <div v-if="isHoveredArtworkBlock" class="overlay">
            <VIcon class="fullscreen-icon" size="48" icon="mdi-fullscreen" />
          </div>
        </transition>
      </div>
      <div v-else-if="!(artworkLoading)" class="pa-2 text-center">
        PDF/AI preview is not available
      </div>
      <!-- Prepress Proof only 1 page regardless of number of sides -->
      <div
        v-if="!['prepress_proof'].includes(props.fileType)"
        class="pdf-pages"
      >
        <template v-for="side in signSides + 1">
          <a
            class="pdf-page"
            :class="{ 'active': side  === page, 'disabled': side > totalPages }"
            @click="side > totalPages ? {} : page = side"
          >
            {{ sides[side - 1] }}
          </a>
        </template>
      </div>
      <!-- /Prepress Proof only 1 page regardless of number of sides -->
    </div>
  </div>
</template>

<style lang="scss">
.pdf-pages {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  .pdf-page {
    cursor: pointer;
    padding: 2px 8px;
    margin: 0 2px;
    border: 1px solid #DC0000;
    border-radius: 50%;
    &.active {
      background: #DC0000;
      color: #fff;
    }
    &.disabled {
      background: rgba(#DC0000, 0.2);
      color: #fff;
      cursor: not-allowed;
    }
  }
}
.artwork-block {
  position: relative;
  width: 100%;
  height: 100%;
  cursor: pointer;
  transition: box-shadow 0.3s ease;
}

.artwork-block:hover {
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  transition: opacity 0.3s ease;
}

.fullscreen-icon {
  color: white;
  opacity: 0.8;
  transition: transform 0.3s ease;
}

.fullscreen-icon:hover {
  transform: scale(1.2);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
